import { type ReactNode, useEffect, useState } from "react";
import { type DashboardProfile } from "./Dashboard";
import { StandFinance } from "./StandFinance";
import { StandCrm, type SaleOpportunitySeed } from "./StandCrm";
import { StandMarketingAnalytics } from "./StandMarketingAnalytics";
import { StandSettings } from "./StandSettings";
import { StandTeam } from "./StandTeam";
import { StandTasks } from "./StandTasks";
import { StandTaskDialog } from "./StandTaskDialog";
import { StandChatDesk } from "./StandChatDesk";
import { StandNotificationCenter } from "./StandNotificationCenter";
import { type OpportunityTask } from "./OpportunityDialog";
import { VehicleStock } from "./VehicleStock";
import { supabase } from "./lib/supabase";
import { runtimeConfig } from "./lib/runtime-config";
import { openTaskConversation, taskAge } from "./task-age";
import { ThemeToggle } from "./ThemeToggle";
import "./stand-dashboard.css";

export type StandMembership = {
  id: string;
  organization_id: string;
  role_key: "stand_master" | "manager" | "sales" | "operations" | "user";
  permissions: string[];
  status: string;
};
type Organization = {
  id: string;
  name: string;
  commercial_status: string;
  country_code: string;
  currency_code: string;
  test_fixture: boolean;
  test_scenario: string | null;
};
type Subscription = {
  id?: string;
  effective_status: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  terms_snapshot: { limits?: Record<string, number | null>; name?: string } | null;
};
type PublicSiteAccess = { enabled: boolean; url: string };
type View =
  | "overview"
  | "stock"
  | "vehicle_circuit"
  | "sold"
  | "archived"
  | "contacts"
  | "clients"
  | "opportunities"
  | "tasks"
  | "marketing"
  | "sales"
  | "team"
  | "settings";
type StandIconName =
  | "overview"
  | "stock"
  | "clients"
  | "tasks"
  | "marketing"
  | "sales"
  | "documents"
  | "team"
  | "settings";
const roleLabels = {
  stand_master: "Master do stand",
  manager: "Gestor",
  sales: "Comercial",
  operations: "Operações",
  user: "Utilizador",
};
const environment = runtimeConfig.appEnv;
const accessLabels: Record<string, string> = {
  active: "Plano ativo",
  trialing: "Degustação ativa",
  read_only: "Apenas leitura",
  suspended: "Suspenso",
  pending: "Pendente",
  cancelled: "Cancelado",
};
const standIconPaths: Record<StandIconName, ReactNode> = {
  overview: (
    <>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </>
  ),
  stock: (
    <>
      <path d="M3 13h18l-2-5H5l-2 5Z" />
      <path d="M5 13v5h14v-5" />
      <circle cx="7" cy="18" r="1" />
      <circle cx="17" cy="18" r="1" />
    </>
  ),
  clients: (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M3 21v-2a6 6 0 0 1 12 0v2" />
      <path d="M17 11a4 4 0 0 1 4 4v2" />
    </>
  ),
  tasks: (
    <>
      <path d="M5 4h14v16H5z" />
      <path d="m8 9 2 2 5-5M8 15h8" />
    </>
  ),
  marketing: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 12V6M12 12l5 3" />
    </>
  ),
  sales: (
    <>
      <path d="M4 19V9" />
      <path d="M10 19V5" />
      <path d="M16 19v-7" />
      <path d="m3 8 6-5 6 7 6-5" />
    </>
  ),
  documents: (
    <>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v5h5" />
      <path d="M9 12h7M9 16h7" />
    </>
  ),
  team: (
    <>
      <circle cx="8" cy="8" r="3" />
      <circle cx="17" cy="9" r="3" />
      <path d="M2 20a6 6 0 0 1 12 0" />
      <path d="M13 20a5 5 0 0 1 9 0" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19 15a2 2 0 0 0 .4 2L17 19.4a2 2 0 0 0-2-.4 2 2 0 0 0-1 1.7v.3h-4v-.3A2 2 0 0 0 9 19a2 2 0 0 0-2 .4L4.6 17A2 2 0 0 0 5 15a2 2 0 0 0-1.7-1H3v-4h.3A2 2 0 0 0 5 9a2 2 0 0 0-.4-2L7 4.6A2 2 0 0 0 9 5a2 2 0 0 0 1-1.7V3h4v.3A2 2 0 0 0 15 5a2 2 0 0 0 2-.4L19.4 7A2 2 0 0 0 19 9a2 2 0 0 0 1.7 1h.3v4h-.3A2 2 0 0 0 19 15Z" />
    </>
  ),
};
function StandNavIcon({ name }: { name: StandIconName }) {
  return (
    <svg
      className="stand-navigation-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {standIconPaths[name]}
    </svg>
  );
}

export function StandDashboard({
  profile,
  membership,
  onSignOut,
  administrationContext = false,
  onExitAdministrationContext,
}: {
  profile: DashboardProfile;
  membership: StandMembership;
  onProfileUpdated: (profile: DashboardProfile) => void;
  onSignOut: () => void;
  administrationContext?: boolean;
  onExitAdministrationContext?: () => void;
}) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [publicSite, setPublicSite] = useState<PublicSiteAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>("overview");
  const [vehiclesMenuOpen, setVehiclesMenuOpen] = useState(true);
  const [commercialMenuOpen, setCommercialMenuOpen] = useState(true);
  const [saleOpportunity, setSaleOpportunity] =
    useState<SaleOpportunitySeed | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [nextTask, setNextTask] = useState<OpportunityTask | null>(null);
  const [pendingTaskCount, setPendingTaskCount] = useState(0);
  const [newTaskCount, setNewTaskCount] = useState(0);
  const [taskCreateRequest, setTaskCreateRequest] = useState(0);
  const [selectedDashboardTask, setSelectedDashboardTask] =
    useState<OpportunityTask | null>(null);
  const [taskLoading, setTaskLoading] = useState(true);
  const [contactAvailable, setContactAvailable] = useState(false);
  useEffect(() => {
    void (async () => {
      await supabase.rpc("synchronize_organization_subscription_access", {
        p_organization_id: membership.organization_id,
      });
      const [org, sub, site] = await Promise.all([
        supabase
          .from("organizations")
          .select(
            "id,name,commercial_status,country_code,currency_code,test_fixture,test_scenario",
          )
          .eq("id", membership.organization_id)
          .single(),
        supabase
          .from("organization_subscription_access")
          .select(
            "effective_status,trial_ends_at,current_period_end,terms_snapshot",
          )
          .eq("organization_id", membership.organization_id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase.rpc("get_site_builder", {
          p_organization_id: membership.organization_id,
        }),
      ]);
      setOrganization(org.data as Organization | null);
      setSubscription(sub.data as Subscription | null);
      const siteData = site.data as { site?: { enabled?: boolean } | null; platform_url?: string } | null;
      setPublicSite(site.error || !siteData?.platform_url ? null : { enabled: Boolean(siteData.site?.enabled), url: siteData.platform_url });
      setLoading(false);
    })();
  }, [membership.organization_id]);
  useEffect(() => {
    const loadNextTask = async () => {
      setTaskLoading(true);
      const { data, count, error } = await supabase
        .from("stand_crm_tasks")
        .select(
          "id,opportunity_id,contact_id,conversation_id,customer_id,vehicle_id,title,description,due_at,priority_starts_at,priority,status,assigned_to,completion_note,withdrawal_reason,review_status,review_note",
          { count: "exact" },
        )
        .eq("organization_id", membership.organization_id)
        .eq("status", "open")
        .order("due_at")
        .limit(1)
        .maybeSingle();
      setNextTask(error ? null : (data as OpportunityTask | null));
      setPendingTaskCount(error ? 0 : (count ?? (data ? 1 : 0)));
      const { data: summary } = await supabase.rpc("my_stand_task_summary", {
        p_organization_id: membership.organization_id,
      });
      if (summary) {
        setPendingTaskCount(Number(summary.total ?? 0));
        setNewTaskCount(Number(summary.new ?? 0));
      }
      setTaskLoading(false);
    };
    void loadNextTask();
    const channel = supabase
      .channel(`stand-dashboard-tasks-${membership.organization_id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "stand_crm_tasks",
          filter: `organization_id=eq.${membership.organization_id}`,
        },
        loadNextTask,
      )
      .subscribe();
    window.addEventListener("vorno:tasks-changed", loadNextTask);
    return () => {
      window.removeEventListener("vorno:tasks-changed", loadNextTask);
      void supabase.removeChannel(channel);
    };
  }, [membership.organization_id]);
  const contactEligible =
    !administrationContext &&
    (membership.role_key === "stand_master" ||
      membership.role_key === "manager" ||
      membership.role_key === "sales" ||
      membership.permissions.includes("*") ||
      membership.permissions.includes("crm.manage"));
  useEffect(() => {
    if (!contactEligible) return;
    let active = true;
    const heartbeat = async (online: boolean) => {
      const { data } = await supabase.rpc("set_my_stand_contact_availability", {
        p_organization_id: membership.organization_id,
        p_accepts: true,
        p_online: online,
      });
      if (active && online && data) setContactAvailable(true);
    };
    void heartbeat(true);
    const timer = setInterval(() => void heartbeat(true), 90000);
    return () => {
      active = false;
      clearInterval(timer);
      void heartbeat(false);
    };
  }, [contactEligible, membership.organization_id]);
  if (loading)
    return (
      <main className="loading-page">
        <span className="loader" />
        <p>A preparar o espaço do stand…</p>
      </main>
    );
  const writable =
    subscription?.effective_status === "active" ||
    subscription?.effective_status === "trialing";
  const hasPermission = (permission: string) =>
    administrationContext ||
    membership.permissions.includes("*") ||
    membership.permissions.includes(permission);
  const canManageStock =
    writable &&
    (hasPermission("stock.manage") ||
      membership.role_key === "stand_master" ||
      membership.role_key === "manager" ||
      membership.role_key === "operations");
  const canManageSales =
    writable &&
    (hasPermission("sales.manage") ||
      membership.role_key === "stand_master" ||
      membership.role_key === "manager" ||
      membership.role_key === "sales");
  const canViewCrm =
    hasPermission("crm.manage") ||
    hasPermission("sales.manage") ||
    membership.role_key === "stand_master" ||
    membership.role_key === "manager" ||
    membership.role_key === "sales";
  const canManageCrm = writable && canViewCrm;
  const canSuperviseCrm =
    administrationContext ||
    hasPermission("crm.supervise") ||
    membership.role_key === "stand_master" ||
    membership.role_key === "manager";
  const canViewTeam =
    hasPermission("users.manage") || membership.role_key === "stand_master";
  const canManageTeam = writable && canViewTeam;
  const canViewSettings =
    hasPermission("settings.manage") || membership.role_key === "stand_master";
  const canManageSettings = writable && canViewSettings;
  const canValidateFinance =
    writable &&
    (hasPermission("finance.manage") || membership.role_key === "stand_master");
  const canViewFinanceCosts =
    writable &&
    (hasPermission("finance.costs") ||
      hasPermission("finance.manage") ||
      membership.role_key === "stand_master");
  const canViewFinanceMargin =
    writable &&
    (hasPermission("finance.margin") ||
      hasPermission("finance.manage") ||
      membership.role_key === "stand_master");
  const canViewFinanceDocuments =
    writable &&
    (hasPermission("finance.documents") ||
      hasPermission("finance.manage") ||
      membership.role_key === "stand_master");
  const canViewFinance =
    hasPermission("finance.view") ||
    hasPermission("finance.manage") ||
    hasPermission("finance.costs") ||
    hasPermission("finance.documents") ||
    hasPermission("finance.margin") ||
    hasPermission("sales.manage") ||
    membership.role_key === "stand_master" ||
    membership.role_key === "manager" ||
    membership.role_key === "sales";
  const canViewDocuments =
    hasPermission("documents.view") ||
    hasPermission("documents.manage") ||
    membership.role_key === "stand_master";
  const canManageDocuments =
    writable &&
    (hasPermission("documents.manage") ||
      membership.role_key === "stand_master");
  const openTask = async (task: OpportunityTask) => {
    const { data } = await supabase.rpc("mark_my_stand_task_seen", {
      p_task_id: task.id,
    });
    if (data) {
      setPendingTaskCount(Number(data.total ?? 0));
      setNewTaskCount(Number(data.new ?? 0));
    }
    if (task.conversation_id) {
      openTaskConversation(task.conversation_id);
      return;
    }
    setSelectedDashboardTask(task);
  };
  const openTasks = async () => {
    const { data } = await supabase.rpc("mark_my_stand_tasks_seen", {
      p_organization_id: membership.organization_id,
    });
    if (data) {
      setPendingTaskCount(Number(data.total ?? 0));
      setNewTaskCount(Number(data.new ?? 0));
    }
    setView("tasks");
  };
  const navButton = (
    target: View,
    label: string,
    icon: StandIconName,
    disabled = false,
  ) => (
    <button
      className={view === target ? "active" : ""}
      onClick={() => (target === "tasks" ? void openTasks() : setView(target))}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      <StandNavIcon name={icon} />
      <span>{label}</span>
    </button>
  );
  return (
    <div className="stand-shell">
      <aside>
        <div className="stand-sidebar-top">
          <div className="stand-brand">
            <strong>Vorno</strong>
            <small>Stand</small>
          </div>
          <ThemeToggle />
        </div>
        {administrationContext && (
          <button
            className="return-to-platform"
            type="button"
            onClick={onExitAdministrationContext}
            title="Voltar à Administração Central"
          >
            <b aria-hidden="true">←</b>
            <span>Administração Central</span>
          </button>
        )}
        <nav>
          {navButton("overview", "Visão geral", "overview")}
          <div className={`stand-nav-group ${vehiclesMenuOpen ? "open" : ""}`} aria-label="Veículos">
            <button className="stand-nav-group-trigger" type="button" onClick={() => setVehiclesMenuOpen((open) => !open)} aria-expanded={vehiclesMenuOpen}>
              <StandNavIcon name="stock" />
              <span>Veículos</span>
              <b aria-hidden="true">⌄</b>
            </button>
            {vehiclesMenuOpen && <div className="stand-nav-submenu">
              <button className={view === "stock" ? "active" : ""} onClick={() => setView("stock")}><i />Stock</button>
              <button className={view === "vehicle_circuit" ? "active" : ""} onClick={() => setView("vehicle_circuit")}><i />Circuito</button>
              <button className={view === "sold" ? "active" : ""} onClick={() => setView("sold")}><i />Vendidos</button>
              <button className={view === "archived" ? "active" : ""} onClick={() => setView("archived")}><i />Arquivados</button>
            </div>}
          </div>
          {canViewCrm && (
            <div className={`stand-nav-group ${commercialMenuOpen ? "open" : ""}`} aria-label="Comercial">
              <button className="stand-nav-group-trigger" type="button" onClick={() => setCommercialMenuOpen((open) => !open)} aria-expanded={commercialMenuOpen}>
                <StandNavIcon name="clients" />
                <span>Comercial</span>
                <b aria-hidden="true">⌄</b>
              </button>
              {commercialMenuOpen && <div className="stand-nav-submenu">
                <button className={view === "contacts" ? "active" : ""} onClick={() => setView("contacts")}><i />Contactos</button>
                <button className={view === "opportunities" ? "active" : ""} onClick={() => setView("opportunities")}><i />Oportunidades</button>
                <button className={view === "clients" ? "active" : ""} onClick={() => setView("clients")}><i />Clientes</button>
                <button className={view === "tasks" ? "active" : ""} onClick={() => setView("tasks")}><i />Tarefas</button>
              </div>}
            </div>
          )}
          {canSuperviseCrm && navButton("marketing", "Marketing", "marketing")}
          {canViewFinance && navButton("sales", "Financeiro", "sales")}
          {canViewTeam && navButton("team", "Equipa", "team")}
          {(canViewSettings || canViewDocuments) &&
            navButton("settings", "Configurações", "settings")}
        </nav>
        <div className={`stand-profile ${profileOpen ? "open" : ""}`}>
          <button
            className="stand-profile-avatar"
            type="button"
            aria-label="Mostrar informações do perfil"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((open) => !open)}
          >
            {(profile.display_name ?? "U")[0].toUpperCase()}
          </button>
          <button
            className="stand-profile-quick-signout"
            type="button"
            onClick={onSignOut}
            title="Terminar sessão"
            aria-label="Terminar sessão"
          >
            ↗
          </button>
          <div className="stand-profile-information">
            <strong>{profile.display_name ?? "Utilizador"}</strong>
            <small>
              {administrationContext
                ? "Super Master · contexto do stand"
                : roleLabels[membership.role_key]}
            </small>
            <em>
              {profile.country_code} · {profile.currency_code} · {environment}
            </em>
          </div>
          <div className="stand-profile-session">
            <span>
              Sessão <b>ON</b>
            </span>
            <button
              type="button"
              onClick={onSignOut}
              title="Terminar sessão"
              aria-label="Terminar sessão"
            >
              <i aria-hidden="true">↗</i>Sair
            </button>
          </div>
        </div>
      </aside>
      <main>
        <div className="stand-access-context stand-top-access-context">
          {organization && (
            <StandNotificationCenter
              organizationId={organization.id}
              enabled={canViewCrm}
              onOpenTask={async (taskId) => {
                const { data, error } = await supabase
                  .from("stand_crm_tasks")
                  .select(
                    "id,opportunity_id,contact_id,conversation_id,customer_id,vehicle_id,title,description,due_at,priority_starts_at,priority,status,assigned_to,completion_note,withdrawal_reason,review_status,review_note",
                  )
                  .eq("organization_id", organization.id)
                  .eq("id", taskId)
                  .maybeSingle();
                if (error || !data) return;
                setSelectedDashboardTask(data as OpportunityTask);
              }}
            />
          )}
          {organization?.test_fixture && organization.test_scenario && (
            <span className="fixture-scenario">Modo Teste</span>
          )}
          <span className={`access-mode ${writable ? "active" : "readonly"}`}>
            {accessLabels[subscription?.effective_status ?? ""] ??
              "Sem plano"}
          </span>
        </div>
        <header>
          <div>
            <p className="eyebrow">
              {administrationContext
                ? "Administração do stand"
                : "Espaço do stand"}
            </p>
            <h1>{organization?.name ?? "Stand"}</h1>
          </div>
          <div className="stand-header-actions">
            {contactEligible && (
              <button
                type="button"
                className={`stand-contact-availability ${contactAvailable ? "online" : "offline"}`}
                onClick={async () => {
                  const next = !contactAvailable;
                  const { error } = await supabase.rpc(
                    "set_my_stand_contact_availability",
                    {
                      p_organization_id: membership.organization_id,
                      p_accepts: true,
                      p_online: next,
                    },
                  );
                  if (!error) setContactAvailable(next);
                }}
              >
                <i />
                {contactAvailable
                  ? "Disponível para contactos"
                  : "Indisponível para contactos"}
              </button>
            )}
            <div
              className={`stand-task-shortcut ${nextTask ? `priority-${taskAge(nextTask.due_at, nextTask.status, nextTask.priority, nextTask.priority_starts_at).effectivePriority}` : ""} ${nextTask && taskAge(nextTask.due_at, nextTask.status, nextTask.priority, nextTask.priority_starts_at).tone === "overdue" ? "overdue" : ""}`}
            >
              <div className="stand-task-label">
                <span>Tarefas</span>
                {writable && (
                  <button
                    type="button"
                    onClick={() => {
                      setTaskCreateRequest((request) => request + 1);
                      setView("tasks");
                    }}
                  >
                    + Nova tarefa
                  </button>
                )}
              </div>
              {taskLoading ? (
                <small>A atualizar…</small>
              ) : nextTask ? (
                <>
                  <button
                    className="stand-task-current"
                    type="button"
                    onClick={() => void openTask(nextTask)}
                  >
                    <strong>{nextTask.title}</strong>
                    <time>
                      {
                        taskAge(
                          nextTask.due_at,
                          nextTask.status,
                          nextTask.priority,
                          nextTask.priority_starts_at,
                        ).label
                      }{" "}
                      ·{" "}
                      {new Date(nextTask.due_at).toLocaleString("pt-PT", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </time>
                  </button>
                  <button
                    className="stand-task-counts"
                    type="button"
                    onClick={() => void openTasks()}
                  >
                    <strong>
                      {pendingTaskCount}{" "}
                      {pendingTaskCount === 1 ? "tarefa" : "tarefas"}
                    </strong>
                    <b>
                      {newTaskCount} {newTaskCount === 1 ? "nova" : "novas"}
                    </b>
                  </button>
                </>
              ) : (
                <small>Sem tarefas pendentes</small>
              )}
            </div>
          </div>
        </header>
        {!writable && (
          <section className="readonly-explanation">
            <strong>
              {subscription?.effective_status === "read_only"
                ? "O período de acesso terminou"
                : "Modo de leitura ativo"}
            </strong>
            <p>
              Pode consultar os dados, mas a criação e alteração permanecem
              bloqueadas até existir um plano ou período experimental ativo.
            </p>
          </section>
        )}
        {view === "overview" ? (
          <>
            <section className="stand-welcome">
              <p className="eyebrow">Visão geral</p>
              <h2>
                {administrationContext
                  ? "Está a administrar este stand como Super Master."
                  : "Bem-vindo ao espaço da sua equipa."}
              </h2>
              <p>
                {administrationContext
                  ? "As alterações ficam registadas com a sua identidade da Administração Central."
                  : "Este painel está separado da administração central. Cada utilizador vê apenas os dados e funções autorizados no respetivo stand."}
              </p>
            </section>
            <section className={`stand-public-site-access ${publicSite?.enabled ? "published" : "unpublished"}`} aria-label="Site público do stand">
              <span aria-hidden="true">↗</span>
              <div>
                <small>Site público</small>
                <strong>{publicSite?.enabled ? "O site do stand está publicado" : "O site do stand ainda não está publicado"}</strong>
              </div>
              {publicSite?.enabled ? (
                <a href={publicSite.url} target="_blank" rel="noreferrer">Abrir site público</a>
              ) : canViewSettings ? (
                <button type="button" onClick={() => setView("settings")}>Configurar site</button>
              ) : (
                <small>A publicação é gerida nas Configurações.</small>
              )}
            </section>
            <section className="stand-metrics">
              <article>
                <small>Função</small>
                <strong>
                  {administrationContext
                    ? "Super Master"
                    : roleLabels[membership.role_key]}
                </strong>
              </article>
              <article>
                <small>Plano</small>
                <strong>
                  {accessLabels[subscription?.effective_status ?? ""] ??
                    "Sem plano ativo"}
                </strong>
              </article>
              <article>
                <small>Região</small>
                <strong>
                  {organization?.country_code ?? profile.country_code} ·{" "}
                  {organization?.currency_code ?? profile.currency_code}
                </strong>
              </article>
            </section>
          </>
        ) : organization && (view === "stock" || view === "vehicle_circuit" || view === "sold" || view === "archived") ? (
          <VehicleStock
            organizationId={organization.id}
            countryCode={organization.country_code}
            currencyCode={organization.currency_code}
            writable={canManageStock}
            roleKey={membership.role_key}
            canManageLifecycle={canManageStock}
            canManageFinance={canValidateFinance}
            vehicleLimit={
              subscription?.terms_snapshot?.limits?.vehicles ?? null
            }
            circuit={view === "sold" ? "sold" : view === "vehicle_circuit" ? "workflow" : view === "archived" ? "archived" : "inventory"}
            onOpenSiteSettings={() => setView("settings")}
          />
        ) : organization && view === "sales" ? (
          <StandFinance
            organizationId={organization.id}
            currencyCode={organization.currency_code}
            canManageSales={canManageSales}
            canManageFinance={canValidateFinance}
            canViewCosts={canViewFinanceCosts}
            canViewMargin={canViewFinanceMargin}
            canViewDocuments={canViewFinanceDocuments}
            canValidate={canValidateFinance}
            saleOpportunity={saleOpportunity}
            onSaleOpportunityConsumed={() => setSaleOpportunity(null)}
          />
        ) : organization &&
          (view === "contacts" || view === "clients" || view === "opportunities") ? (
          <StandCrm
            key={view}
            organizationId={organization.id}
            countryCode={organization.country_code === "BR" ? "BR" : "PT"}
            writable={canManageCrm}
            canSupervise={canSuperviseCrm}
            initialTab={
              view === "opportunities"
                ? "opportunities"
                : view === "clients"
                  ? "customers"
                  : "contacts"
            }
            onConvertToSale={(seed) => {
              setSaleOpportunity(seed);
              setView("sales");
            }}
          />
        ) : organization && view === "tasks" ? (
          <StandTasks
            organizationId={organization.id}
            writable={writable}
            canSupervise={canSuperviseCrm}
            createRequest={taskCreateRequest}
          />
        ) : organization && view === "marketing" ? (
          <StandMarketingAnalytics organizationId={organization.id} />
        ) : organization && view === "team" ? (
          <StandTeam
            organizationId={organization.id}
            canManage={canManageTeam}
            userLimit={subscription?.terms_snapshot?.limits?.users ?? null}
          />
        ) : (
          organization && (
            <StandSettings
              organizationId={organization.id}
              canManage={canManageSettings}
              canManageSubscription={administrationContext || membership.role_key === "stand_master" || hasPermission("settings.manage")}
              canViewConfiguration={canViewSettings}
              canViewDocuments={canViewDocuments}
              canManageDocuments={canManageDocuments}
            />
          )
        )}
        {organization && selectedDashboardTask && (
          <StandTaskDialog
            task={selectedDashboardTask}
            organizationId={organization.id}
            writable={canManageCrm}
            canSupervise={canSuperviseCrm}
            onClose={() => setSelectedDashboardTask(null)}
            onChanged={async () => {
              setSelectedDashboardTask(null);
              const [{ data }, { data: summary }] = await Promise.all([
                supabase
                  .from("stand_crm_tasks")
                  .select(
                    "id,opportunity_id,contact_id,conversation_id,customer_id,vehicle_id,title,description,due_at,priority_starts_at,priority,status,assigned_to,completion_note,withdrawal_reason,review_status,review_note",
                  )
                  .eq("organization_id", organization.id)
                  .eq("status", "open")
                  .order("due_at")
                  .limit(1)
                  .maybeSingle(),
                supabase.rpc("my_stand_task_summary", {
                  p_organization_id: organization.id,
                }),
              ]);
              setNextTask(data as OpportunityTask | null);
              if (summary) {
                setPendingTaskCount(Number(summary.total ?? 0));
                setNewTaskCount(Number(summary.new ?? 0));
              }
            }}
          />
        )}
        {organization && (
          <StandChatDesk
            organizationId={organization.id}
            enabled={canViewCrm}
          />
        )}
      </main>
    </div>
  );
}
